<script lang="ts">
  import { createMotionScope, enterFrom, exitTo, prefersReducedMotion } from '../../game/motion';
  import { feedback } from '../../platform/feedback';
  import { i18n } from '../i18n/index.svelte';
  import { toasts, type Toast } from '../stores/toasts.svelte';
  import SystemToast from './SystemToast.svelte';

  /**
   * The global toast layer. Mounted once, above every view and outside the
   * element that view transitions animate — a toast that slid away with the
   * screen it was announcing would be unreadable exactly when it matters.
   */

  const t = $derived(i18n.t);
  let stack: HTMLDivElement;
  const scope = createMotionScope();

  const SOUND: Readonly<Record<Toast['kind'], 'ui.confirm' | 'ui.invalid' | 'system.error'>> = {
    success: 'ui.confirm',
    info: 'ui.confirm',
    warning: 'ui.invalid',
    error: 'system.error',
  };

  $effect(() => {
    // Reading `items` is what subscribes this effect; the work below only
    // touches nodes that are new since the last run.
    const current = toasts.items;

    scope.add(() => {
      for (const toast of current) {
        const node = stack?.querySelector<HTMLElement>(`[data-toast-id="${toast.id}"]`);
        // The "have we animated this one already" flag lives on the node rather
        // than in a Set beside it: the node is created and discarded by the
        // keyed `{#each}`, so the flag cannot outlive what it describes or go
        // stale when an id is reused.
        if (!node || node.dataset.entered === 'true') continue;
        node.dataset.entered = 'true';

        feedback.emit(SOUND[toast.kind]);
        enterFrom(node, 'up');
      }
    });
  });

  $effect(() => () => scope.destroy());

  /**
   * Dismissal plays the exit *before* the store drops the item, so the toast
   * animates away instead of disappearing between frames. Under reduced motion
   * the tween is ~instant and this collapses to a plain removal.
   */
  function dismiss(toast: Toast): void {
    const node = stack?.querySelector<HTMLElement>(`[data-toast-id="${toast.id}"]`);
    if (!node || prefersReducedMotion()) {
      toasts.dismiss(toast.id);
      return;
    }

    scope.add(() => {
      exitTo(node, 'up', { onComplete: () => toasts.dismiss(toast.id) });
    });
  }

  function runAction(toast: Toast): void {
    toast.action?.run();
    dismiss(toast);
  }
</script>

<!--
  `aria-live="polite"` on the container rather than per toast: the region has to
  exist in the DOM before the message lands for a screen reader to announce it,
  which a toast that mounts with its text already in place would miss.
-->
<div
  class="toast-host"
  bind:this={stack}
  role="region"
  aria-live="polite"
  aria-label={t.common.ariaNotifications}
>
  {#each toasts.items as toast (toast.id)}
    <div class="toast-host__item" data-toast-id={toast.id}>
      <SystemToast
        kind={toast.kind}
        message={toast.message}
        action={toast.action?.label}
        onaction={() => runAction(toast)}
        ondismiss={() => dismiss(toast)}
      />
    </div>
  {/each}
</div>
